import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { DecisionAuthority, PrescriptionRowView } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { ChipComponent } from '../common/chip.component';
import {
  DECISION_AUTHORITY_HINT_KEY,
  DECISION_AUTO_ICON,
  DECISION_HINT_KEY,
  DECISION_ICON,
  DECISION_LABEL_KEY,
  DECISION_TONE,
  FULFILLMENT_ICON,
  FULFILLMENT_LABEL_KEY,
  FULFILLMENT_TONE,
  SERVICE_KIND_ICON,
  SERVICE_KIND_LABEL_KEY,
} from '../common/guarantee-vocabulary';
import { isAutoGuaranteed } from '../common/prescription-decisions';

/**
 * The rail says only "a different record from the block above" — it carries no meaning of its own,
 * which is why it is drawn from the three families this UI gives no semantic job. A green or red
 * rail beside a ruling chip would read as a second opinion on the ruling.
 *
 * Cycled by adjacency rather than hashed from the guarantee id: a hash can seat two neighbours on
 * one colour, which is the single thing the rail exists to prevent.
 */
const RAIL_TONES = ['bg-purple-500', 'bg-pink-500', 'bg-yellow-600'] as const;

/**
 * Every service in the loaded window as one table, so prescriptions can be read across guarantees
 * instead of one record at a time. The stream answers what happened at a visit; this answers what
 * was ordered for the member, which is a question no other mode is shaped to take.
 *
 * The record behind a row is present only as the day it was raised. Nothing else about the visit is
 * carried — that is the whole point, and adding a column back is how this becomes the stream again.
 *
 * The ruling and delivery keep their own columns rather than folding into one status: they are
 * orthogonal, so a line refused after it was already delivered is a state a single chip could not
 * report without choosing which half to hide.
 *
 * Read-only. Acting on a line means the care plan it belongs to, so a row hands its guarantee back.
 */
@Component({
  selector: 'guarantees-prescription-table',
  standalone: true,
  imports: [DatePipe, MatIcon, MatTooltip, TranslatePipe, ChipComponent],
  templateUrl: './prescription-table.component.html',
  /** On the host, not the grid below it: a container query resolves against an ancestor, never itself. */
  host: { class: '@container/table block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionTableComponent {
  rows = input.required<PrescriptionRowView[]>();

  /** The table does not own navigation: opening a row hands the guarantee back to the page. */
  guaranteeOpened = output<string>();

  protected readonly kindIcons = SERVICE_KIND_ICON;
  protected readonly kindLabelKeys = SERVICE_KIND_LABEL_KEY;

  protected readonly decisionTones = DECISION_TONE;
  protected readonly decisionIcons = DECISION_ICON;
  protected readonly decisionLabelKeys = DECISION_LABEL_KEY;
  protected readonly decisionHintKeys = DECISION_HINT_KEY;

  /** Who granted it is the news on an automatic grant — the chip's own word already says the rest. */
  protected readonly autoIcon = DECISION_AUTO_ICON;
  protected readonly autoHintKey = DECISION_AUTHORITY_HINT_KEY[DecisionAuthority.Auto];

  protected readonly fulfillmentTones = FULFILLMENT_TONE;
  protected readonly fulfillmentIcons = FULFILLMENT_ICON;
  protected readonly fulfillmentLabelKeys = FULFILLMENT_LABEL_KEY;

  /**
   * The rows with the record boundaries marked, so a run of services ordered together reads as one
   * block. The rows arrive sorted by the day the record was raised and every service on a record
   * shares that day, which is what keeps a record's services adjacent.
   *
   * Compared against the row immediately above rather than against a set of records already seen:
   * were a record ever to arrive split, this gives it a second band instead of silently rejoining
   * two runs that are not next to each other.
   */
  protected readonly bands = computed(() => {
    let previousGuaranteeId: string | undefined;
    let tone = -1;

    return this.rows().map(row => {
      const startsRecord = row.guaranteeId !== previousGuaranteeId;
      if (startsRecord) {
        previousGuaranteeId = row.guaranteeId;
        tone = (tone + 1) % RAIL_TONES.length;
      }

      return { row, startsRecord, rail: RAIL_TONES[tone], auto: isAutoGuaranteed(row.line.prescription) };
    });
  });
}
